import {
  type DropdownOption,
  type EventCardProps,
  ActionBar,
  ChakraBox,
  DateFormat,
  EventCard,
  FAIconName,
  getAgeFromDob,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';

import type { RecurringAppointmentUpdateOption } from '~/lib/constants/calendar.constants';
import {
  DEFAULT_AVAILABILITY_SETTINGS_TIMEZONE_CODE,
  filterSupportAppointments,
  formatDateWithTimezone,
  getAppointmentEndTime,
  getQueryPeriods,
  Recurrence,
} from '~/lib/util/calendar.util';
import { formatAppointmentTimeFromDate, getDifferenceInDays } from '../util';
import { capitalizeFirstLetter } from '~/lib/common';
import { getRecurrenceDisplay } from '~/lib/util/schedule/schedule.util';
import { SCHEDULE_CONFLICT_TEXT } from '~/lib/constants/scheduling.constants';
import { ConflictErrors } from '../SchedulerV2';
import errorConstant from '~/lib/constants/error.constants';
import useCalendarApi from '~/lib/hooks/useCalendarApi';
import { useEffect, useState } from 'react';
import { ParticipantTypes } from '~/lib/constants/schedule.constants';
import { useGetRecurringAppointmentDateTimesByAppointmentExternalIdQuery } from '~/store/services/appointment';
import {
  type ScheduledAppointments,
  filterPatientAppointmentsWithEditingEventId,
  getIncludableAvailableSlots,
  hasPatient,
} from './util';

export interface EditAppointmentSummaryProps {
  appointmentStartDateTime: string;
  availabilityQuery?: CalendarResourceConfigAvailabilityQuery;
  elementToken: string;
  eventTitle: string;
  hostAttendee?: ScheduleUserBase | Participant;
  id: string;
  isFetchingConflicts: (isFetching: boolean) => void;
  recurrenceOption: DropdownOption;
  editAppointment: ScheduleItemBase;
  selectedAppointmentType: AppointmentType;
  scheduledAppointments: ScheduledAppointments;
  shouldEditAllAppointments: RecurringAppointmentUpdateOption;
  shouldShowEditErrorMessage: boolean;
  updatedAttendees?: ScheduleUserBase[];
  attendingParticipants?: ScheduleUserBase[];
}

const EditAppointmentSummary = ({
  attendingParticipants,
  appointmentStartDateTime,
  availabilityQuery,
  elementToken,
  eventTitle,
  hostAttendee,
  isFetchingConflicts,
  recurrenceOption,
  editAppointment,
  scheduledAppointments,
  selectedAppointmentType,
  updatedAttendees,
  shouldShowEditErrorMessage = false,
  id: TOP_LEVEL_ID,
}: EditAppointmentSummaryProps) => {
  const { appointmentRecurrenceCheck } = useCalendarApi();

  const frequency = recurrenceOption.value as Recurrence;

  const {
    data: recurringAppointmentDateTimes,
    isFetching: isFetchingRecurringAppointmentDateTimes,
  } = useGetRecurringAppointmentDateTimesByAppointmentExternalIdQuery(
    editAppointment.externalId,
    {
      skip:
        !editAppointment.externalId || frequency === Recurrence.DOES_NOT_REPEAT,
    },
  );

  const summaryAttendees =
    (updatedAttendees || attendingParticipants)?.map((each) => ({
      ...each,
      name: capitalizeFirstLetter(each?.name),
      userType: each.userType || each.type,
    })) ?? [];

  const { patient, recurringUntilDateTime } = editAppointment;

  const { patientAppointments, supportAppointments } = scheduledAppointments;

  const [conflicts, setConflicts] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const appointmentEndDateTime = getAppointmentEndTime(
    appointmentStartDateTime,
    selectedAppointmentType?.durationInMinutes,
  );

  const getHostAttendeeTimeZone = () => {
    if (hostAttendee && 'timezoneDetails' in hostAttendee) {
      return hostAttendee.timezoneDetails?.code;
    }
    return (hostAttendee as Participant)?.timezone;
  };

  const getConflicts = async () => {
    if (frequency === Recurrence.DOES_NOT_REPEAT) return;

    isFetchingConflicts(true);
    setIsLoading(true);

    try {
      const patientAppointmentsToCheck = hasPatient({
        attendingParticipants,
        updatedAttendees,
      })
        ? filterPatientAppointmentsWithEditingEventId({
            eventId: editAppointment.eventId,
            patientAppointments,
          })
        : [];

      const supportIDs = attendingParticipants
        ?.filter(({ type }) => type === ParticipantTypes.SUPPORT)
        .map(({ externalId }) => externalId);

      const supportAppointmentsToCheck = filterSupportAppointments(
        supportIDs,
        supportAppointments,
        editAppointment.eventId,
      );

      const includeAvailableSlots = getIncludableAvailableSlots({
        appointmentRecurrenceSlots: recurringAppointmentDateTimes,
        editAppointment,
      });
      const conflictDates = await appointmentRecurrenceCheck(
        availabilityQuery,
        elementToken,
        patientAppointmentsToCheck,
        getQueryPeriods(
          getAppointmentEndTime(
            appointmentStartDateTime,
            selectedAppointmentType.durationInMinutes,
          ),
          frequency,
          appointmentStartDateTime,
          getHostAttendeeTimeZone() ??
            DEFAULT_AVAILABILITY_SETTINGS_TIMEZONE_CODE,
          editAppointment.recurringUntilDateTime,
        ),
        supportAppointmentsToCheck,
        includeAvailableSlots,
      );
      setConflicts(conflictDates);
    } catch (error) {
      console.error('Error fetching appointment conflicts: ', error);
    } finally {
      setIsLoading(false);
      isFetchingConflicts(false);
    }
  };

  useEffect(() => {
    if (!isFetchingRecurringAppointmentDateTimes) getConflicts();
  }, [isFetchingRecurringAppointmentDateTimes]);

  const shouldShowEndDate: boolean =
    getDifferenceInDays(recurringUntilDateTime) <= 90;

  const eventCardProps: EventCardProps = {
    eventType: 'appointment',
    scheduleItem: {
      attendees: summaryAttendees,
      eventDate: formatDateWithTimezone(
        appointmentStartDateTime,
        `${DateFormat.weekdayShortMonthDateNoSpecialChar}`,
        undefined,
      ),
      eventExternalId: selectedAppointmentType.appointmentTypeExtId,
      eventName: eventTitle,
      eventTime: `${formatAppointmentTimeFromDate(appointmentStartDateTime)} - ${formatAppointmentTimeFromDate(appointmentEndDateTime)}`,
      frequencyText: getRecurrenceDisplay(
        appointmentStartDateTime,
        frequency,
        editAppointment?.recurringUntilDateTime,
        true,
        shouldShowEndDate,
      ),
      patient: {
        age: getAgeFromDob(patient?.dateOfBirth),
        name: `${capitalizeFirstLetter(patient?.chosenName || patient?.firstName)} ${capitalizeFirstLetter(patient?.lastName)}`,
      },
      shouldShowViewPatientProfileButton: false,
      shouldShowZoomButton: false,
    },
  };

  const renderConflicts = () => {
    const conflict = (
      <Text>
        {SCHEDULE_CONFLICT_TEXT}{' '}
        <span style={{ fontWeight: 500 }}>{conflicts.join(', ')}</span>
      </Text>
    );
    return (
      <ChakraBox marginTop="24px">
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          id={`${TOP_LEVEL_ID}__${ConflictErrors.OVERLAPPED_DATES_ERROR}`}
          size="small"
          text={conflict}
          variant="warning"
        />
      </ChakraBox>
    );
  };

  const renderErrorMessage = () => {
    return (
      <ChakraBox marginTop="24px">
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          id={`${TOP_LEVEL_ID}__${ConflictErrors.OVERLAPPED_DATES_ERROR}`}
          size="medium"
          text={errorConstant.ErrorBookAppointment}
          variant="critical"
        />
      </ChakraBox>
    );
  };

  return (
    <SkeletonLoader
      id={`${TOP_LEVEL_ID}__edit-summary`}
      isLoaded={!isLoading && !isFetchingRecurringAppointmentDateTimes}
    >
      <EventCard {...eventCardProps} />
      {conflicts.length > 0 && renderConflicts()}
      {shouldShowEditErrorMessage && renderErrorMessage()}
    </SkeletonLoader>
  );
};

export default EditAppointmentSummary;
